// Metadata endpoints: model, field types, apps, objects, fields, relations, pipelines on the
// contracts of `@dataroom/contracts/metadata` (owner i18n-metadata in wave 1, data-model-objects in
// wave 2). Reads return labels in the member's language; `raw.*` returns them as written (plain or
// `{ en, de }`, permission `metadata.write`) for editors. `api.call(<contract>, …)` works the same;
// this group keeps the short names its callers use.
import type {
  App,
  CreateAppInput,
  CreateFieldInput,
  CreateLinkFieldInput,
  CreateObjectTypeInput,
  CreatePipelineInput,
  CreateRelationInput,
  CreateStageInput,
  Field,
  ObjectDetail,
  ObjectTypeSummary,
  Pipeline,
  RawApp,
  RawField,
  RawObjectDetail,
  RawObjectType,
  RawPipeline,
  RawRelation,
  RawWorkspaceModel,
  Relation,
  UpdateAppInput,
  UpdateFieldInput,
  UpdateLinkFieldInput,
  UpdateObjectTypeInput,
  UpdatePipelineInput,
  UpdateRelationInput,
  UpdateStageInput,
  WorkspaceModel,
} from '@dataroom/core';
import {
  archiveFieldContract,
  archiveObjectContract,
  createAppContract,
  createFieldContract,
  createLinkFieldContract,
  createObjectContract,
  createPipelineContract,
  createRelationContract,
  createStageContract,
  deleteAppContract,
  deleteLinkFieldContract,
  deletePipelineContract,
  deleteRelationContract,
  deleteStageContract,
  getFieldContract,
  getModelContract,
  getObjectContract,
  getPipelineContract,
  getRelationContract,
  listAppsContract,
  listFieldTypesContract,
  listFieldsContract,
  listObjectsContract,
  listPipelinesContract,
  listRelationsContract,
  reorderFieldsContract,
  reorderStagesContract,
  updateAppContract,
  updateFieldContract,
  updateLinkFieldContract,
  updateObjectContract,
  updatePipelineContract,
  updateRelationContract,
  updateStageContract,
} from '@dataroom/contracts/metadata';
import type { Http } from './http';
import { createCall } from './typed';

export const metadataApi = (http: Http) => {
  const call = createCall(http);
  return {
    model: () => call(getModelContract) as Promise<WorkspaceModel>,
    /** Texts as written (plain or localized), for editors (`metadata.write`). */
    raw: {
      model: () => call(getModelContract, { query: { raw: true } }) as Promise<RawWorkspaceModel>,
      apps: () => call(listAppsContract, { query: { raw: true } }) as Promise<RawApp[]>,
      objects: () =>
        call(listObjectsContract, { query: { raw: true } }) as Promise<Array<ObjectTypeSummary & RawObjectType>>,
      object: (key: string) =>
        call(getObjectContract, { params: { key }, query: { raw: true } }) as Promise<RawObjectDetail>,
      fields: (objectKey: string, opts: { includeArchived?: boolean } = {}) =>
        call(listFieldsContract, {
          params: { key: objectKey },
          query: { raw: true, ...(opts.includeArchived ? { includeArchived: true } : {}) },
        }) as Promise<RawField[]>,
      field: (objectKey: string, fieldKey: string) =>
        call(getFieldContract, { params: { key: objectKey, fieldKey }, query: { raw: true } }) as Promise<RawField>,
      relations: (object?: string) =>
        call(listRelationsContract, { query: { raw: true, ...(object ? { object } : {}) } }) as Promise<
          RawRelation[]
        >,
      relation: (id: string) =>
        call(getRelationContract, { params: { id }, query: { raw: true } }) as Promise<RawRelation>,
      pipelines: (object?: string) =>
        call(listPipelinesContract, { query: { raw: true, ...(object ? { object } : {}) } }) as Promise<
          RawPipeline[]
        >,
      pipeline: (id: string) =>
        call(getPipelineContract, { params: { id }, query: { raw: true } }) as Promise<RawPipeline>,
    },
    fieldTypes: () => call(listFieldTypesContract),
    apps: {
      list: () => call(listAppsContract) as Promise<App[]>,
      create: (input: CreateAppInput) => call(createAppContract, { body: input }),
      update: (id: string, input: UpdateAppInput) => call(updateAppContract, { params: { id }, body: input }),
      delete: (id: string) => call(deleteAppContract, { params: { id } }),
    },
    objects: {
      list: () => call(listObjectsContract) as Promise<ObjectTypeSummary[]>,
      get: (key: string) => call(getObjectContract, { params: { key } }) as Promise<ObjectDetail>,
      create: (input: CreateObjectTypeInput) => call(createObjectContract, { body: input }),
      update: (key: string, input: UpdateObjectTypeInput) =>
        call(updateObjectContract, { params: { key }, body: input }),
      /** Refused (409 `object_in_use` with the dependency report) while other objects depend on it. */
      archive: (key: string) => call(archiveObjectContract, { params: { key } }),
    },
    fields: {
      list: (objectKey: string, opts: { includeArchived?: boolean } = {}) =>
        call(listFieldsContract, {
          params: { key: objectKey },
          query: opts.includeArchived ? { includeArchived: true } : {},
        }) as Promise<Field[]>,
      get: (objectKey: string, fieldKey: string) =>
        call(getFieldContract, { params: { key: objectKey, fieldKey } }) as Promise<Field>,
      create: (objectKey: string, input: CreateFieldInput) =>
        call(createFieldContract, { params: { key: objectKey }, body: input }),
      update: (objectKey: string, fieldKey: string, input: UpdateFieldInput) =>
        call(updateFieldContract, { params: { key: objectKey, fieldKey }, body: input }),
      /** Refused (409 `field_in_use` with the dependents) while expressions read it, unless `force`. */
      archive: (objectKey: string, fieldKey: string, opts: { force?: boolean } = {}) =>
        call(archiveFieldContract, {
          params: { key: objectKey, fieldKey },
          query: opts.force ? { force: true } : {},
        }),
      reorder: (objectKey: string, ids: string[]) =>
        call(reorderFieldsContract, { params: { key: objectKey }, body: { ids } }),
    },
    relations: {
      list: (object?: string) =>
        call(listRelationsContract, { query: object ? { object } : {} }) as Promise<Relation[]>,
      get: (id: string) => call(getRelationContract, { params: { id } }) as Promise<Relation>,
      create: (input: CreateRelationInput) => call(createRelationContract, { body: input }),
      update: (id: string, input: UpdateRelationInput) =>
        call(updateRelationContract, { params: { id }, body: input }),
      delete: (id: string) => call(deleteRelationContract, { params: { id } }),
      createLinkField: (id: string, input: CreateLinkFieldInput) =>
        call(createLinkFieldContract, { params: { id }, body: input }),
      updateLinkField: (id: string, linkFieldId: string, input: UpdateLinkFieldInput) =>
        call(updateLinkFieldContract, { params: { id, linkFieldId }, body: input }),
      deleteLinkField: (id: string, linkFieldId: string) =>
        call(deleteLinkFieldContract, { params: { id, linkFieldId } }),
    },
    pipelines: {
      list: (object?: string) =>
        call(listPipelinesContract, { query: object ? { object } : {} }) as Promise<Pipeline[]>,
      get: (id: string) => call(getPipelineContract, { params: { id } }) as Promise<Pipeline>,
      create: (input: CreatePipelineInput) => call(createPipelineContract, { body: input }),
      update: (id: string, input: UpdatePipelineInput) =>
        call(updatePipelineContract, { params: { id }, body: input }),
      delete: (id: string) => call(deletePipelineContract, { params: { id } }),
      createStage: (id: string, input: CreateStageInput) =>
        call(createStageContract, { params: { id }, body: input }),
      updateStage: (id: string, stageId: string, input: UpdateStageInput) =>
        call(updateStageContract, { params: { id, stageId }, body: input }),
      deleteStage: (id: string, stageId: string) => call(deleteStageContract, { params: { id, stageId } }),
      reorderStages: (id: string, ids: string[]) =>
        call(reorderStagesContract, { params: { id }, body: { ids } }),
    },
  };
};
